import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader } from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Trans, useLingui } from '@lingui/react/macro';
import * as Sentry from '@sentry/react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useCallback, useEffect, useMemo, type ComponentProps, type FC, type ReactNode } from 'react';

// ── Context Bridge ─────────────────────────────────────────────────────────
// Captures context provider wrappers from inside the component tree so that
// modals rendered by NiceModal (at the root) can re-provide them.

type WrapperFn = (children: ReactNode) => ReactNode;

let contextWrappers: WrapperFn[] = [];

/**
 * Register a context wrapper that will be applied around every modal's content.
 * Call from inside a provider to bridge its context into modals.
 *
 * Usage:
 * ```tsx
 * export const MyProvider = ({ children, value }) => {
 *   useModalContextBridge((c) => <MyContext.Provider value={value}>{c}</MyContext.Provider>);
 *   return <MyContext.Provider value={value}>{children}</MyContext.Provider>;
 * };
 * ```
 */
export function useModalContextBridge(wrapper: WrapperFn) {
	useEffect(() => {
		contextWrappers = [...contextWrappers, wrapper];
		return () => {
			contextWrappers = contextWrappers.filter((w) => w !== wrapper);
		};
	}, [wrapper]);
}

export function applyContextBridge(children: ReactNode): ReactNode {
	return contextWrappers.reduce((acc, wrap) => wrap(acc), children);
}

type FactoryOptions = {
	closeOnClickOutside?: boolean;
	closeOnEscape?: boolean;
	className?: string;
	withCloseButton?: boolean;
};

// Reserved prop every createModal instance accepts: called when the user closes the modal through
// Escape, outside-press, or the close button — not on programmatic remove(), so callers can react
// to a dismissal without overloading unmount.
type ModalCloseProps = { onModalClose?: () => void };

type CompositionComponents = {
	Header: FC<ComponentProps<'div'>>;
	Footer: FC<ComponentProps<'div'>>;
	Title: FC<ComponentProps<'div'>>;
	Description: FC<ComponentProps<'div'>>;
};

type ModalComponent<P extends object> = FC<P & CompositionComponents>;

function MobileTitle({ className, ...props }: ComponentProps<'div'>) {
	return <div data-slot="drawer-title" className={cn('text-foreground text-base font-medium', className)} {...props} />;
}

function MobileDescription({ className, ...props }: ComponentProps<'div'>) {
	return <div data-slot="drawer-description" className={cn('text-muted-foreground text-sm', className)} {...props} />;
}

function ModalErrorFallback({
	error,
	resetError,
	onClose,
}: {
	error: unknown;
	resetError: () => void;
	onClose: () => void;
}) {
	const { t } = useLingui();
	return (
		<div className="p-6 text-center space-y-4">
			<p className="text-destructive font-medium">
				<Trans>Something went wrong</Trans>
			</p>
			<p className="text-sm text-muted-foreground">
				{error instanceof Error ? error.message : t`An unexpected error occurred`}
			</p>
			<div className="flex gap-2 justify-center">
				<button
					onClick={resetError}
					className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
				>
					<Trans>Try again</Trans>
				</button>
				<button onClick={onClose} className="px-4 py-2 text-sm rounded-md border hover:bg-accent">
					<Trans>Close</Trans>
				</button>
			</div>
		</div>
	);
}

/**
 * Creates a modal component using NiceModal for imperative modal management.
 *
 * Usage:
 * ```tsx
 * const MyModal = createModal<{ title: string }>(({ title, Header, Footer, Title, Description }) => {
 *   const modal = useModal();
 *   return (
 *     <>
 *       <Header>
 *         <Title>{title}</Title>
 *         <Description>Some description</Description>
 *       </Header>
 *       <div>Content</div>
 *       <Footer>
 *         <Button onClick={() => modal.remove()}>Close</Button>
 *       </Footer>
 *     </>
 *   );
 * });
 *
 * // Usage
 * NiceModal.show(MyModal, { title: "Hello World" });
 * ```
 */
export const createModal = <P extends object>(component: ModalComponent<P>, factoryOptions?: FactoryOptions) => {
	const { closeOnClickOutside = true, closeOnEscape = true, className, withCloseButton = true } = factoryOptions ?? {};
	const Component = component;

	return NiceModal.create<P & ModalCloseProps>((props) => {
		const modal = useModal();
		const isMobile = useIsMobile();
		const { onModalClose } = props;

		const componentsMap = useMemo(() => {
			return {
				Title: isMobile ? MobileTitle : DialogTitle,
				Header: isMobile ? DrawerHeader : DialogHeader,
				Footer: isMobile ? DrawerFooter : DialogFooter,
				Description: isMobile ? MobileDescription : DialogDescription,
			};
		}, [isMobile]);

		const dismissByUser = useCallback(() => {
			onModalClose?.();
			modal.remove();
		}, [modal, onModalClose]);

		const handleOpenChange = useCallback(
			(open: boolean, eventDetails?: { reason?: string }) => {
				if (open) return;
				const reason = eventDetails?.reason;
				if (reason === 'escape-key' && !closeOnEscape) return;
				if (reason === 'outside-press' && !closeOnClickOutside) return;
				dismissByUser();
			},
			[dismissByUser],
		);

		const modalContent = applyContextBridge(
			<Sentry.ErrorBoundary
				fallback={({ error, resetError }) => (
					<ModalErrorFallback error={error} resetError={resetError} onClose={() => modal.remove()} />
				)}
			>
				<Component
					{...props}
					Header={componentsMap.Header}
					Footer={componentsMap.Footer}
					Title={componentsMap.Title}
					Description={componentsMap.Description}
				/>
			</Sentry.ErrorBoundary>,
		);

		if (isMobile) {
			return (
				<Drawer swipeDirection="down" modal={false} open={modal.visible} onOpenChange={handleOpenChange}>
					{modal.visible && (
						<div
							aria-hidden="true"
							className="fixed inset-0 z-50 bg-black/10 supports-backdrop-filter:backdrop-blur-xs"
							onClick={() => {
								if (!closeOnClickOutside) return;
								dismissByUser();
							}}
						/>
					)}
					<DrawerContent className={cn(className, 'm-1 px-2')}>{modalContent}</DrawerContent>
				</Drawer>
			);
		}

		return (
			<Dialog open={modal.visible} onOpenChange={handleOpenChange}>
				<DialogContent className={className} showCloseButton={withCloseButton}>
					{modalContent}
				</DialogContent>
			</Dialog>
		);
	});
};

export { useModal } from '@ebay/nice-modal-react';
